import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { KIND, SIZE as BUTTON_SIZE, StyledLoadingSpinner } from 'baseui/button'
import { Cell, Grid } from 'baseui/layout-grid'
import { FormControl } from 'baseui/form-control'
import { ModalHeader, ModalBody, ModalButton } from 'baseui/modal'
import { StyledTable } from 'baseui/table'
import { StyledBodyCell } from 'baseui/table-grid'
import { Textarea, SIZE } from 'baseui/textarea'
import { Input } from 'baseui/input'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import {
  CommentActionsRow,
  CommentMetaLabel,
  CommentRowContainer,
  CommentText,
  CommentsLoadingContainer,
  CommentsScrollContainer,
} from './styled-components'

export interface CommentItem {
  id?: number
  comment: string
  created_at?: string
  createdAt?: string
  user?: { id?: number; firstname?: string; name?: string }
  user_id?: number
}

interface CommentsModalContentProps {
  comments: CommentItem[]
  onSubmit: (comment: string, callback: (success: boolean) => void) => void
  onUpdate?: (commentId: number, comment: string, callback: (success: boolean) => void) => void
  onDelete?: (commentId: number, callback: (success: boolean) => void) => void
  canMutate?: (comment: CommentItem) => boolean
  isLoadingComments?: boolean
}

const CommentsModalContent = ({
  comments,
  onSubmit,
  onUpdate,
  onDelete,
  canMutate,
  isLoadingComments = false,
}: CommentsModalContentProps) => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [savingEditId, setSavingEditId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)


  const handleResponse = (success: boolean) => {
    setLoading(false)
    if (success) {
      setComment('')
      return
    }
    setError(t('subscriptionTable:save_error'))
  }

  const saveCommentHandler = () => {
    setLoading(true)
    setError('')
    if (comment.length === 0) {
      setError(t('subscriptionTable:mandatory_field'))
      setLoading(false)
      return
    }
    onSubmit(comment, handleResponse)
  }

  const startEditing = (row: CommentItem) => {
    if (row.id === undefined) return
    setEditingId(row.id)
    setEditingValue(row.comment)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditingValue('')
  }

  const submitEdit = (commentId: number) => {
    if (!onUpdate || editingValue.length === 0) return
    setSavingEditId(commentId)
    onUpdate(commentId, editingValue, success => {
      setSavingEditId(null)
      if (success) {
        cancelEditing()
      }
    })
  }

  const submitDelete = (commentId: number) => {
    if (!onDelete) return
    setDeletingId(commentId)
    onDelete(commentId, () => {
      setDeletingId(null)
    })
  }

  const reversedComments = [...comments].reverse()

  return (
    <>
      <ModalHeader>{t('subscriptionTable:comments')}</ModalHeader>
      <ModalBody>
        <Grid>
          <Cell span={12}>
            <CommentsScrollContainer>
              <StyledTable role="grid">
                {isLoadingComments ? (
                  <CommentsLoadingContainer>
                    <StyledLoadingSpinner />
                  </CommentsLoadingContainer>
                ) : reversedComments.length === 0 ? (
                  <CommentRowContainer role="row">
                    <StyledBodyCell>
                      <CommentText>{t('subscriptionTable:no_comments')}</CommentText>
                    </StyledBodyCell>
                  </CommentRowContainer>
                ) : (
                  reversedComments.map((row, index) => {
                    const date = (row.created_at || row.createdAt)
                      ? new Date(row.created_at || row.createdAt || '').toLocaleString('fr-FR')
                      : ''
                    const createdBy =
                      row.user ? `${row.user.firstname ?? ''} ${row.user.name ?? ''}`.trim() : ''
                    const isEditing = editingId !== null && editingId === row.id
                    const showActions =
                      typeof row.id === 'number' && (onUpdate || onDelete) && (canMutate ? canMutate(row) : true)

                    return (
                      <CommentRowContainer role="row" key={row.id ?? index}>
                        <StyledBodyCell $striped={index % 2 === 0}>
                          {isEditing ? (
                            <Input
                              value={editingValue}
                              onChange={e => setEditingValue((e.target as HTMLInputElement).value)}
                              size={BUTTON_SIZE.compact}
                            />
                          ) : (
                            <CommentText>{row.comment}</CommentText>
                          )}
                          <CommentMetaLabel>{date}</CommentMetaLabel>
                          {createdBy && <CommentMetaLabel>{createdBy}</CommentMetaLabel>}
                          {showActions && (
                            <CommentActionsRow>
                              {isEditing ? (
                                <>
                                  {/* @ts-expect-error SDButton is a JS component */}
                                  <SDButton
                                    kind={KIND.primary}
                                    size={BUTTON_SIZE.mini}
                                    onClick={() => submitEdit(row.id as number)}
                                    isLoading={savingEditId === row.id}
                                    disabled={savingEditId === row.id}
                                  >
                                    {t('common:save')}
                                  </SDButton>
                                  {/* @ts-expect-error SDButton is a JS component */}
                                  <SDButton
                                    kind={KIND.secondary}
                                    size={BUTTON_SIZE.mini}
                                    onClick={cancelEditing}
                                    disabled={savingEditId === row.id}
                                  >
                                    {t('common:cancel')}
                                  </SDButton>
                                </>
                              ) : (
                                <>
                                  {onUpdate && (
                                    /* @ts-expect-error SDButton is a JS component */
                                    <SDButton
                                      kind={KIND.secondary}
                                      size={BUTTON_SIZE.mini}
                                      onClick={() => startEditing(row)}
                                      disabled={deletingId === row.id}
                                    >
                                      {t('common:edit')}
                                    </SDButton>
                                  )}
                                  {onDelete && (
                                    /* @ts-expect-error SDButton is a JS component */
                                    <SDButton
                                      kind={KIND.secondary}
                                      size={BUTTON_SIZE.mini}
                                      onClick={() => submitDelete(row.id as number)}
                                      isLoading={deletingId === row.id}
                                      disabled={deletingId === row.id}
                                    >
                                      {t('common:delete')}
                                    </SDButton>
                                  )}
                                </>
                              )}
                            </CommentActionsRow>
                          )}
                        </StyledBodyCell>
                      </CommentRowContainer>
                    )
                  })
                )}
              </StyledTable>
            </CommentsScrollContainer>
          </Cell>
          <Cell span={12}>
            <FormControl label={t('subscriptionTable:add_comment')} error={error}>
              <Textarea
                value={comment}
                onChange={e => setComment((e.target as HTMLTextAreaElement).value)}
                size={SIZE.mini}
                placeholder={t('subscriptionTable:write_comment')}
                clearable
              />
            </FormControl>
            <FormControl>
              <ModalButton
                className={css({ width: '100%' })}
                isLoading={isLoading}
                onClick={saveCommentHandler}
                size={SIZE.mini}
              >
                {t('subscriptionTable:validate_comment')}
              </ModalButton>
            </FormControl>
          </Cell>
        </Grid>
      </ModalBody>
    </>
  )
}

export default CommentsModalContent
